import { Monitor, Moon, Sun, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useThemePreferenceOptional } from "@/contexts/ThemePreferenceContext";
import type { ThemePreference } from "@/lib/theme-preference";

const THEME_PILL_OPTIONS: { value: ThemePreference; icon: LucideIcon; label: string }[] = [
  { value: "auto", icon: Monitor, label: "Match system" },
  { value: "light", icon: Sun, label: "Light mode" },
  { value: "dark", icon: Moon, label: "Dark mode" },
];

/**
 * Whether an {@link AppearanceToggle} with these props will render anything.
 * Lets surrounding chrome (e.g. a dropdown separator) gate itself on the same
 * condition without duplicating the controlled-vs-context resolution.
 */
export function useAppearanceToggleVisible(
  themePreferenceProp?: ThemePreference,
  onThemePreferenceChangeProp?: (value: ThemePreference) => void | Promise<void>,
): boolean {
  const themeFromCtx = useThemePreferenceOptional();
  const controlled =
    themePreferenceProp !== undefined && onThemePreferenceChangeProp !== undefined;
  return controlled || !!themeFromCtx;
}

export interface AppearanceToggleProps {
  /**
   * Storybook / tests: bypasses ThemePreferenceProvider when both are set.
   * In production both surfaces (desktop dropdown + mobile panel) leave these
   * undefined and read the preference from ThemePreferenceContext.
   */
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (value: ThemePreference) => void | Promise<void>;
  /**
   * Active-segment styling.
   * - `'subtle'` (default): the nav-dropdown look (`bg-background` selected) —
   *   keep for the desktop dropdown + mobile panel callers.
   * - `'primary'`: the design-system Segmented look (`var(--gradient-primary)`
   *   selected, like PillTabs) — used on the /settings Preferences tab.
   */
  variant?: 'subtle' | 'primary';
  className?: string;
}

/**
 * Segmented theme-preference control (auto / light / dark) shared by the
 * desktop {@link UserProfileMenu} dropdown and the mobile burger panel so the
 * appearance control stays pixel-identical across breakpoints.
 *
 * Renders nothing when there is no theme source (no controlled props and no
 * surrounding ThemePreferenceProvider).
 */
export function AppearanceToggle({
  themePreference: themePreferenceProp,
  onThemePreferenceChange: onThemePreferenceChangeProp,
  variant = 'subtle',
  className,
}: AppearanceToggleProps) {
  const themeFromCtx = useThemePreferenceOptional();
  const controlled =
    themePreferenceProp !== undefined && onThemePreferenceChangeProp !== undefined;
  const themePreference = controlled ? themePreferenceProp : themeFromCtx?.preference;
  const showAppearance = controlled || !!themeFromCtx;

  const handleThemeChange = async (next: ThemePreference) => {
    if (controlled) {
      await Promise.resolve(onThemePreferenceChangeProp!(next));
      return;
    }
    await themeFromCtx?.setPreference(next);
  };

  if (!showAppearance || themePreference === undefined) return null;

  return (
    <div
      className={cn(
        "flex h-8 overflow-hidden rounded-full border border-border/50 bg-muted/50 p-px",
        className,
      )}
      role="group"
      aria-label="Appearance"
    >
      {THEME_PILL_OPTIONS.map(({ value, icon: Icon, label }, i) => {
        const selected = themePreference === value;
        return (
          <button
            key={value}
            type="button"
            aria-label={label}
            aria-pressed={selected}
            className={cn(
              "flex flex-1 items-center justify-center outline-none transition-colors focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-popover",
              i === 0 && "rounded-l-full",
              i === THEME_PILL_OPTIONS.length - 1 && "rounded-r-full",
              selected
                ? variant === "primary"
                  ? "shadow-sm"
                  : "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            style={
              selected && variant === "primary"
                ? {
                    background: "var(--gradient-primary)",
                    color: "var(--primary-foreground)",
                  }
                : undefined
            }
            onClick={() => void handleThemeChange(value)}
          >
            <Icon className="size-4 shrink-0" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
